// eslint-disable-next-line no-restricted-imports
import { FaroDialog } from "@components/common/dialog/faro-dialog";
import { Button, neutral } from "@flat-ui";
import { alpha } from "@mui/material";
import { ActiveMembersInfoBar } from "@pages/settings/subscription-settings/active-members/active-members-info-bar";
import { ActiveMembersTable } from "@pages/settings/subscription-settings/active-members/active-members-table";
import { QueryParams } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { resetActiveMembersState } from "@store/active-members/active-members-slice";
import { fetchActiveMembers } from "@store/active-members/active-members-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getBooleanQueryParam, removeQueryParam } from "@utils/url-utils";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ActiveMembersExport } from "@pages/settings/subscription-settings/active-members/active-members-export";

interface Props {
  /** Members constraint */
  membersConstraint: FaroCoreApiTypes.ConstraintQuota;
}

/**
 * A dialog to show active members under the subscription plan.
 */
export function ActiveMembersDialog({ membersConstraint }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { companyId } = useAppParams();
  const { trackEvent } = useTrackEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(
    getBooleanQueryParam(searchParams.toString(), QueryParams.activeMembers)
  );

  // Fetch active members when the dialog is opened every time
  useEffect(() => {
    if (isDialogOpen && companyId) {
      void dispatch(fetchActiveMembers({ companyId }));
    }
  }, [companyId, dispatch, isDialogOpen]);

  /** Handle button clicks to open the dialog */
  function handleDialogOpen(): void {
    trackEvent({
      name: WorkspaceEvents.viewActiveMembers,
      props: {
        origin: "subscription-settings-page",
      },
    });
    setIsDialogOpen(true);

    // Set the query param to indicate that the dialog is open
    searchParams.set(QueryParams.activeMembers, "true");
    setSearchParams(searchParams);
  }

  function onCLose(): void {
    setIsDialogOpen(false);

    // Remove the query param
    removeQueryParam(QueryParams.activeMembers);

    // Clearing active members state while closing the dialog
    dispatch(resetActiveMembersState());
  }

  return (
    <>
      <Button
        variant="ghost"
        size="m"
        onClick={handleDialogOpen}
        sx={{
          padding: "8px 16px",
          fontSize: "12px",
          fontWeight: 600,
          lineHeight: "18px",
          gap: "8px",
          "&:hover": {
            borderRadius: "4px",
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            backgroundColor: alpha(neutral[500], 0.15),
          },
        }}
      >
        View active members
      </Button>

      {/* At the moment, it’s not possible to add an icon next to the X button, so we still need to use FaroDialog */}
      <FaroDialog
        title="Active members"
        open={isDialogOpen}
        onClose={onCLose}
        shouldHideActions={true}
        fullWidth={true}
        maxWidth="xl"
        topRightActions={<ActiveMembersExport />}
        sx={{
          "& .MuiDialog-paper": {
            height: "100%",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiDialogContent-root": {
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "auto",
          },
        }}
      >
        <ActiveMembersInfoBar maxNumberOfMembers={membersConstraint.limit} />

        <ActiveMembersTable />
      </FaroDialog>
    </>
  );
}
